import React, { useState, useRef, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  TextField,
  Typography,
  Checkbox,
  FormControlLabel,
  Snackbar,
  Alert,
  InputAdornment,
  Paper,
  List,
  ListItem,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SearchIcon from "@mui/icons-material/Search";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import DeleteIcon from "@mui/icons-material/Delete";
import AttachmentIcon from "@mui/icons-material/Attachment";
import DownloadIcon from "@mui/icons-material/Download";
import { styled } from "@mui/material/styles";
import { ProgramOption } from "../api/types";
import { fetchPrograms } from "../api/faqApi";
import Papa from "papaparse";
import { createFaq } from "../api/faqApi";
import DownloadCsvButton from "./DownloadCsvButton";

interface CsvFaq {
  question: string;
  answer: string;
}

interface UploadCsvDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: {
    faqs: CsvFaq[];
    globalVisibility: boolean;
    selectedPrograms: ProgramOption[];
    csvFile: File | null;
  }) => void;
}

const icon = <CheckBoxOutlineBlankIcon sx={{ fontSize: 20 }} />;
const checkedIcon = <CheckBoxIcon sx={{ fontSize: 20 }} />;

const ActionBar = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "flex-end",
  gap: theme.spacing(1.5),
  padding: theme.spacing(2, 3, 2, 3),
  borderTop: "1px solid #eee",
  background: "#fff",
}));

const FaqPreviewBox = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
  padding: theme.spacing(2),
  border: "1px solid #e0e0e0",
  borderRadius: "8px",
  maxHeight: "260px",
  overflowY: "auto",
  backgroundColor: "#f9fbfc",
}));

const UploadCsvDialog: React.FC<UploadCsvDialogProps> = ({
  open,
  onClose,
  onSave,
}) => {
  const [faqs, setFaqs] = useState<CsvFaq[]>([]);
  const [globalVisibility, setGlobalVisibility] = useState(false);
  const [selectedPrograms, setSelectedPrograms] = useState<ProgramOption[]>([]);
  const [tempSelectedPrograms, setTempSelectedPrograms] = useState<
    ProgramOption[]
  >([]);
  const [inputValue, setInputValue] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [programs, setPrograms] = useState<ProgramOption[]>([]);
  const [programFetchError, setProgramFetchError] = useState<string | null>(
    null
  );
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [errors, setErrors] = useState({
    csvFile: false,
    programs: false,
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error" | "info" | "warning",
  });
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editValues, setEditValues] = useState<{
    question: string;
    answer: string;
  }>({ question: "", answer: "" });
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedFaqIndex, setSelectedFaqIndex] = useState<number | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [faqToDelete, setFaqToDelete] = useState<number | null>(null);
  const [uploading, setUploading] = useState(false);

  const loadAllPrograms = async () => {
    setLoading(true);
    setProgramFetchError(null);
    try {
      const allPrograms = await fetchPrograms("");
      setPrograms(allPrograms);
    } catch (error) {
      console.error("Error loading programs:", error);
      setProgramFetchError("Failed to load programs. Please try again.");
      setPrograms([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && !globalVisibility) {
      loadAllPrograms();
    }
  }, [open, globalVisibility]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    // Check if file is a CSV
    if (file.type !== "text/csv" && !file.name.toLowerCase().endsWith(".csv")) {
      setSnackbar({
        open: true,
        message: "Please upload a valid CSV file. Only .csv files are allowed.",
        severity: "error",
      });
      // Reset the file input
      event.target.value = "";
      setCsvFile(null);
      return;
    }

    setCsvFile(file);
    setErrors({ ...errors, csvFile: false });

    // Read the CSV file
    Papa.parse(file, {
      complete: (results) => {
        if (results.data && results.data.length > 0) {
          // Get headers from first row
          const headers = results.data[0] as string[];
          const requiredColumns = ["question", "answer"];

          // Check if headers contain required columns
          const hasRequiredColumns = requiredColumns.every((column) =>
            headers.some(
              (header) => header.toLowerCase().trim() === column.toLowerCase()
            )
          );

          if (!hasRequiredColumns) {
            setSnackbar({
              open: true,
              message: "CSV file must contain 'question' and 'answer' columns",
              severity: "error",
            });
            setCsvFile(null);
            event.target.value = "";
            return;
          }

          // Get indices of question and answer columns
          const questionIndex = headers.findIndex(
            (h) => h.toLowerCase().trim() === "question"
          );
          const answerIndex = headers.findIndex(
            (h) => h.toLowerCase().trim() === "answer"
          );

          // Skip the header row and convert CSV data to FAQ objects
          const parsedFaqs = results.data
            .slice(1) // Skip the header row
            .filter((row: any) => {
              return (
                row[questionIndex] &&
                row[answerIndex] &&
                typeof row[questionIndex] === "string" &&
                typeof row[answerIndex] === "string"
              );
            })
            .map((row: any) => ({
              question: row[questionIndex].trim(),
              answer: row[answerIndex].trim(),
            }));

          if (parsedFaqs.length > 0) {
            setFaqs(parsedFaqs);
            setSnackbar({
              open: true,
              message: `Successfully loaded ${parsedFaqs.length} FAQs from CSV`,
              severity: "success",
            });
          } else {
            setSnackbar({
              open: true,
              message:
                "No valid FAQs found in the CSV file. Please ensure your CSV has data in the question and answer columns",
              severity: "error",
            });
            setCsvFile(null);
            event.target.value = "";
          }
        } else {
          setSnackbar({
            open: true,
            message: "CSV file is empty or has no data",
            severity: "error",
          });
          setCsvFile(null);
          event.target.value = "";
        }
      },
      error: (error) => {
        console.error("Error parsing CSV:", error);
        setSnackbar({
          open: true,
          message: `Error reading CSV file: ${error.message}`,
          severity: "error",
        });
        setCsvFile(null);
        event.target.value = "";
      },
      header: false, // We'll handle headers manually
      skipEmptyLines: true,
    });
  };

  const handleProgramSelect = (program: ProgramOption) => {
    const isSelected = tempSelectedPrograms.some(
      (p) => p.value === program.value
    );
    if (isSelected) {
      setTempSelectedPrograms(
        tempSelectedPrograms.filter((p) => p.value !== program.value)
      );
    } else {
      setTempSelectedPrograms([...tempSelectedPrograms, program]);
    }
  };

  const handleSelectAll = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setTempSelectedPrograms(programs);
  };

  const handleClearAll = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setTempSelectedPrograms([]);
    setInputValue("");
  };

  const handleDone = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedPrograms(tempSelectedPrograms);
    setInputValue(formatInputValue(tempSelectedPrograms));
    setIsOpen(false);
  };

  const formatInputValue = (selected: ProgramOption[]): string => {
    if (selected.length === 0) return "";
    return `${selected.length} selected`;
  };

  const handleInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const results = await fetchPrograms(value);
        setPrograms(results);
      } catch (error) {
        console.error("Error searching programs:", error);
        setProgramFetchError("Failed to search programs. Please try again.");
      } finally {
        setLoading(false);
      }
    }, 300);
  };

  const handleSave = async () => {
    setErrors({
      csvFile: false,
      programs: false,
    });

    const newErrors = {
      csvFile: !csvFile,
      programs: !globalVisibility && selectedPrograms.length === 0,
    };

    setErrors(newErrors);

    if (Object.values(newErrors).some((error) => error)) {
      return;
    }

    setUploading(true);

    try {
      const userDetailsStr = localStorage.getItem("maiaskUser") || "{}";
      let userDetails;
      try {
        userDetails = JSON.parse(userDetailsStr);
      } catch (e) {
        console.error("Error parsing user details:", e);
        userDetails = {};
      }

      const payload = {
        faqs: faqs.map((faq) => ({
          question: faq.question.trim(),
          answer: faq.answer.trim(),
        })),
        globalVisibility,
        selectedPrograms,
        email: userDetails.email || "",
      };

      const response = await createFaq(payload);
      if (response.output?.status) {
        setSnackbar({
          open: true,
          message: "FAQs uploaded successfully",
          severity: "success",
        });
        // Reset state and close dialog
        setFaqs([]);
        setGlobalVisibility(false);
        setSelectedPrograms([]);
        setTempSelectedPrograms([]);
        setInputValue("");
        setCsvFile(null);
        setErrors({ csvFile: false, programs: false });
        onClose();
      } else {
        throw new Error(response.output?.message || "Failed to upload FAQs");
      }
    } catch (error) {
      console.error("Error uploading FAQs:", error);
      setSnackbar({
        open: true,
        message:
          error instanceof Error
            ? error.message
            : "Failed to upload FAQs. Please try again.",
        severity: "error",
      });
    } finally {
      setUploading(false);
    }
  };

  const handleCloseSnackbar = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  const handleMenuClick = (
    event: React.MouseEvent<HTMLElement>,
    index: number
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedFaqIndex(index);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedFaqIndex(null);
  };

  const handleEdit = () => {
    if (selectedFaqIndex !== null) {
      setEditingIndex(selectedFaqIndex);
      setEditValues({
        question: faqs[selectedFaqIndex].question,
        answer: faqs[selectedFaqIndex].answer,
      });
    }
    handleMenuClose();
  };

  const handleDelete = () => {
    if (selectedFaqIndex !== null) {
      setFaqToDelete(selectedFaqIndex);
      setDeleteConfirmOpen(true);
    }
    handleMenuClose();
  };

  const handleDeleteConfirm = () => {
    if (faqToDelete !== null) {
      const newFaqs = faqs.filter((_, index) => index !== faqToDelete);
      setFaqs(newFaqs);
      setDeleteConfirmOpen(false);
      setFaqToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setFaqToDelete(null);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: "80vw",
            height: "85vh",
            maxWidth: "80vw",
            maxHeight: "85vh",
            p: 0,
            overflow: "visible",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: "#f7f7f7",
            fontWeight: 700,
            fontSize: 20,
            pb: 2,
            pt: 3,
            px: 3,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid #eee",
            borderRadius: "6px 6px 0 0",
          }}
        >
          Upload CSV
          <IconButton onClick={onClose} sx={{ ml: 2 }}>
            <CloseIcon sx={{ fontSize: 24 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 3, py: 3, overflowY: "auto", flexGrow: 1 }}>
          <Box sx={{ my: 3 }}>
            <Box
              sx={{
                display: "flex",
                gap: 8,
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box display="flex" gap={1}>
                <Typography sx={{ fontWeight: 500, my: 1, fontSize: 14 }}>
                  Upload CSV File <span style={{ color: "red" }}>*</span>
                </Typography>
                <input
                  accept=".csv"
                  style={{ display: "none" }}
                  id="csv-file-upload"
                  type="file"
                  onChange={handleFileChange}
                />
                <label htmlFor="csv-file-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    sx={{
                      border: "1px solid #000",
                      color: "#000",
                      textTransform: "none",
                      "&:hover": {
                        border: "1px solid #000",
                        backgroundColor: "#f5f5f5",
                      },
                    }}
                  >
                    <AttachmentIcon
                      sx={{ mr: 1, fontSize: 20, transform: "rotate(-45deg)" }}
                    />
                    Upload
                  </Button>
                </label>
              </Box>
              <Box
                display="flex"
                flexDirection="column"
                alignItems="end"
                justifyContent="center"
              >
                <DownloadCsvButton />
              </Box>
            </Box>
            {csvFile && (
              <span style={{ paddingLeft: 10, fontSize: 14 }}>
                Selected file: {csvFile.name}
              </span>
            )}
            {errors.csvFile && (
              <Typography color="error" sx={{ mt: 1, fontSize: 12 }}>
                Please upload a CSV file
              </Typography>
            )}
            {faqs.length > 0 && (
              <FaqPreviewBox>
                {faqs.map((faq, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mb: 1,
                      p: 2,
                      bgcolor: "#fff",
                      borderRadius: 1,
                      boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
                      border: "1px solid #eee",
                    }}
                  >
                    {editingIndex === index ? (
                      <Box
                        sx={{
                          flex: 1,
                          display: "flex",
                          flexDirection: "column",
                          gap: 1,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            sx={{ color: "#111" }}
                          >
                            Question
                          </Typography>
                          <TextField
                            fullWidth
                            value={editValues.question}
                            onChange={(e) =>
                              setEditValues({
                                ...editValues,
                                question: e.target.value,
                              })
                            }
                            size="small"
                            variant="outlined"
                            sx={{ mb: 1 }}
                          />
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            sx={{ color: "#111" }}
                          >
                            Answer
                          </Typography>
                          <TextField
                            fullWidth
                            value={editValues.answer}
                            onChange={(e) =>
                              setEditValues({
                                ...editValues,
                                answer: e.target.value,
                              })
                            }
                            size="small"
                            variant="outlined"
                          />
                        </Box>
                      </Box>
                    ) : (
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            fontSize: 16,
                            color: "#111",
                            mb: 0.5,
                          }}
                        >
                          {faq.question}
                        </Typography>
                        <Typography sx={{ fontSize: 15, color: "#444" }}>
                          {faq.answer}
                        </Typography>
                      </Box>
                    )}
                    <Box
                      sx={{
                        ml: 2,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "100%",
                      }}
                    >
                      {editingIndex === index ? (
                        <>
                          <Tooltip title="Save" placement="right" arrow>
                            <IconButton
                              aria-label="save"
                              size="small"
                              sx={{ color: "#000" }}
                              onClick={() => {
                                const newFaqs = [...faqs];
                                newFaqs[index] = {
                                  ...newFaqs[index],
                                  ...editValues,
                                };
                                setFaqs(newFaqs);
                                setEditingIndex(null);
                              }}
                            >
                              <SaveIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Cancel" placement="right" arrow>
                            <IconButton
                              aria-label="cancel"
                              size="small"
                              sx={{ color: "#000" }}
                              onClick={() => setEditingIndex(null)}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      ) : (
                        <>
                          <IconButton
                            aria-label="more"
                            size="small"
                            sx={{ color: "#000" }}
                            onClick={(e) => handleMenuClick(e, index)}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                          <Menu
                            anchorEl={anchorEl}
                            open={
                              Boolean(anchorEl) && selectedFaqIndex === index
                            }
                            onClose={handleMenuClose}
                            PaperProps={{
                              sx: {
                                boxShadow: "0px 4px 16px rgba(0,0,0,0.12)",
                                borderRadius: "6px",
                              },
                            }}
                          >
                            <MenuItem
                              onClick={handleEdit}
                              sx={{ fontSize: 14 }}
                            >
                              <EditIcon fontSize="small" sx={{ mr: 1 }} />
                              Edit
                            </MenuItem>
                            {faqs.length > 1 && (
                              <MenuItem
                                onClick={handleDelete}
                                sx={{ fontSize: 14, color: "#d32f2f" }}
                              >
                                <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
                                Delete
                              </MenuItem>
                            )}
                          </Menu>
                        </>
                      )}
                    </Box>
                  </Box>
                ))}
              </FaqPreviewBox>
            )}
          </Box>

          <FormControlLabel
            control={
              <Checkbox
                checked={globalVisibility}
                onChange={(e) => setGlobalVisibility(e.target.checked)}
                sx={{
                  color: "#000",
                  "&.Mui-checked": {
                    color: "#000",
                  },
                }}
              />
            }
            label={
              <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                Global Visibility
              </Typography>
            }
          />

          <Box
            sx={{
              border: "1px solid #d3e1e6",
              borderRadius: "10px",
              p: 2,
              mt: 1,
              mb: 3,
              bgcolor: "#fff",
            }}
          >
            <Box sx={{ position: "relative" }}>
              <TextField
                fullWidth
                value={inputValue}
                onChange={handleInputChange}
                onClick={(e) => {
                  e.stopPropagation();
                  setIsOpen(true);
                  if (programs.length === 0 && !loading && !programFetchError) {
                    loadAllPrograms();
                  }
                }}
                onFocus={(e) => {
                  e.stopPropagation();
                  setIsOpen(true);
                  if (programs.length === 0 && !loading && !programFetchError) {
                    loadAllPrograms();
                  }
                }}
                variant="outlined"
                placeholder="Search Programs to Add/Remove"
                disabled={globalVisibility}
                InputProps={{
                  sx: {
                    border: errors.programs
                      ? "1px solid #d32f2f"
                      : "1px solid #d3e1e6",
                    fontSize: 16,
                    bgcolor: "#f9fbfc",
                    borderRadius: "8px",

                    boxSizing: "border-box",
                  },
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: "#bdbdbd", fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  background: "#f9fbfc",
                  borderRadius: "8px",
                  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                  border: "1px solid #d3e1e6",
                }}
              />
              {isOpen && !globalVisibility && (
                <Paper
                  sx={{
                    position: "absolute",
                    width: "100%",
                    mt: 1,
                    overflowY: "hidden",
                    zIndex: 1000,
                    boxShadow: "0px 4px 16px rgba(0,0,0,0.12)",
                    borderRadius: "6px",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  {loading ? (
                    <Box sx={{ p: 2, textAlign: "center" }}>
                      <Typography>Loading...</Typography>
                    </Box>
                  ) : programFetchError ? (
                    <Box
                      sx={{ p: 2, textAlign: "center", color: "error.main" }}
                    >
                      <Typography>{programFetchError}</Typography>
                    </Box>
                  ) : (
                    <>
                      <Box
                        sx={{
                          maxHeight: 180,
                          overflowY: "auto",
                          flexGrow: 1,
                        }}
                      >
                        {programs.length === 0 && !loading && (
                          <Box
                            sx={{ p: 2, textAlign: "center", color: "#888" }}
                          >
                            <Typography>No programs found.</Typography>
                          </Box>
                        )}
                        <List disablePadding>
                          {programs.map((program) => (
                            <ListItem
                              key={program.value}
                              sx={{
                                py: 0.5,
                                px: 2,
                                fontSize: 14,
                                fontWeight: 400,
                                display: "flex",
                                alignItems: "center",
                                minHeight: 36,
                                cursor: "pointer",
                                "&:hover": {
                                  backgroundColor: "#f5f5f5",
                                },
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleProgramSelect(program);
                              }}
                            >
                              <Checkbox
                                icon={icon}
                                checkedIcon={checkedIcon}
                                checked={tempSelectedPrograms.some(
                                  (p) => p.value === program.value
                                )}
                                sx={{ mr: 2, p: 0 }}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  handleProgramSelect(program);
                                }}
                              />
                              {program.label}
                              <span
                                style={{
                                  fontSize: "14px",
                                  color: "#777",
                                  marginLeft: "4px",
                                }}
                              >
                                ({program.code})
                              </span>
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                      <ActionBar
                        sx={{
                          zIndex: 1,
                          background: "#fff",
                          borderTop: "1px solid #eee",
                          flexShrink: 0,
                        }}
                      >
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelectAll(e);
                          }}
                          sx={{ fontWeight: 600, color: "#000" }}
                        >
                          Select All
                        </Button>
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleClearAll(e);
                          }}
                          sx={{
                            fontWeight: 600,
                            color: "#000",
                            border: "1px solid #000",
                            borderRadius: "4px",
                          }}
                        >
                          Clear All
                        </Button>
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDone(e);
                          }}
                          variant="contained"
                          sx={{
                            fontWeight: 600,
                            bgcolor: "#000",
                            color: "#fff",
                            boxShadow: "none",
                            ":hover": { bgcolor: "#222" },
                          }}
                        >
                          Done
                        </Button>
                      </ActionBar>
                    </>
                  )}
                </Paper>
              )}
            </Box>
            {errors.programs && (
              <Typography color="error" sx={{ mt: 1, fontSize: 12 }}>
                Please select at least one program or enable global visibility
              </Typography>
            )}
            {!globalVisibility && selectedPrograms.length === 0 && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: 120,
                  color: "#bdbdbd",
                  mt: 2,
                  textAlign: "center",
                }}
              >
                <img
                  src="/svgs/faqData.svg"
                  alt="No programs selected"
                  style={{ width: 18, height: 18, marginBottom: "8px" }}
                />
                <Typography fontSize={14} color="#929292">
                  No programs selected. Search and select programs or enable
                  Global Visibility.
                </Typography>
              </Box>
            )}
            {!globalVisibility && selectedPrograms.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption">Selected:</Typography>
                <List dense sx={{ maxHeight: 80, overflowY: "auto" }}>
                  {selectedPrograms.map((p) => (
                    <ListItem key={p.value} dense sx={{ py: 0 }}>
                      <Typography variant="body2">{p.label}</Typography>
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </Box>
        </DialogContent>
        <Box sx={{ borderTop: "1px solid #eee", flexShrink: 0 }} />
        <DialogActions sx={{ justifyContent: "flex-end", px: 3, py: 2 }}>
          <Button
            onClick={onClose}
            variant="outlined"
            sx={{
              borderRadius: "6px",
              minWidth: 100,
              fontSize: 14,
              fontWeight: 600,
              mr: 2,
              bgcolor: "#fff",
              color: "#000",
              borderColor: "#000",
              ":hover": { bgcolor: "#f7f7f7", borderColor: "#000" },
              padding: "16px 30px",
              height: "42px",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={uploading}
            sx={{
              borderRadius: 2,
              minWidth: 100,
              fontSize: 14,
              fontWeight: 600,
              bgcolor: "#000",
              color: "#fff",
              boxShadow: "none",
              ":hover": { bgcolor: "#222" },
              padding: "16px 30px",
              height: "42px",
            }}
          >
            {uploading ? "Uploading" : "Upload"}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleDeleteCancel}
        PaperProps={{
          sx: {
            borderRadius: "6px",
            p: 2,
          },
        }}
      >
        <DialogTitle sx={{ fontSize: 18, fontWeight: 600, p: 0 }}>
          Delete FAQ
        </DialogTitle>
        <DialogContent sx={{ p: 0, mt: 2 }}>
          <Typography>Are you sure you want to delete this FAQ?</Typography>
        </DialogContent>
        <DialogActions sx={{ p: 0, mt: 3, gap: 1 }}>
          <Button
            onClick={handleDeleteCancel}
            variant="outlined"
            sx={{
              borderRadius: "6px",
              minWidth: 100,
              fontSize: 14,
              fontWeight: 600,
              bgcolor: "#fff",
              color: "#000",
              borderColor: "#000",
              ":hover": { bgcolor: "#f7f7f7", borderColor: "#000" },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            sx={{
              borderRadius: "6px",
              minWidth: 100,
              fontSize: 14,
              fontWeight: 600,
              bgcolor: "#000",
              color: "#fff",
              boxShadow: "none",
              ":hover": { bgcolor: "#111" },
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity as "success" | "error"}
          icon={
            snackbar.severity === "success" ? (
              <CheckCircleIcon
                sx={{ width: 20, height: 20, color: "#4BAE4F" }}
              />
            ) : undefined
          }
          sx={{
            width: "100%",
            backgroundColor:
              snackbar.severity === "success" ? "#000" : undefined,
            borderRadius: "6px",
            fontSize: 16,
            color: snackbar.severity === "success" ? "#fff" : undefined,
            "& .MuiAlert-icon": {
              color: snackbar.severity === "success" ? "#fff" : undefined,
            },
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default UploadCsvDialog;
